// Unless explicitly stated otherwise all files in this repository are licensed under the MIT License.
// This product includes software developed at Datadog (https://www.datadoghq.com/).
// Copyright 2019-Present Datadog, Inc.

import { INJECTED_FILE_RX } from '@dd/core/constants';
import { isXpack } from '@dd/core/helpers/bundlers';
import {
    InjectPosition,
    type GetInternalPlugins,
    type GetPluginsArg,
    type PluginOptions,
    type ToInjectItem,
} from '@dd/core/types';

import { PLUGIN_NAME } from './constants';
import { getEsbuildPlugin } from './esbuild';
import { prepareInjections, getContentToInject, hasBeforeAfterInjection } from './helpers';
import { getRollupPlugin } from './rollup';
import type { ContentsToInject } from './types';
import { getXpackPlugin } from './xpack';

export { PLUGIN_NAME } from './constants';

export const getInjectionPlugins: GetInternalPlugins = (arg: GetPluginsArg) => {
    const { bundler, context } = arg;
    const log = context.getLogger(PLUGIN_NAME);
    // Storage for all the injections.
    const injections: ToInjectItem[] = [];

    // Storage for all the positional contents we want to inject.
    const contentsToInject: ContentsToInject = [];

    context.inject = (item: ToInjectItem) => {
        injections.push(item);
    };

    const plugin: PluginOptions = {
        name: PLUGIN_NAME,
        enforce: 'post',
        // Bundler specific part of the plugin.
        // We use it to:
        // - Inject the content in the right places, each bundler offers this differently.
        esbuild: getEsbuildPlugin(log, context, contentsToInject),
        webpack: getXpackPlugin(bundler, log, context, injections, contentsToInject),
        rspack: getXpackPlugin(bundler, log, context, injections, contentsToInject),
        rollup: getRollupPlugin(log, contentsToInject),
        vite: {
            ...(getRollupPlugin(log, contentsToInject) as PluginOptions['vite']),
            enforce: 'pre',
            transformIndexHtml: {
                order: 'pre',
                handler() {
                    // For Vite, we inject MIDDLE content by adding a script tag
                    // that references the virtual injected file
                    const middleContent = getContentToInject(
                        contentsToInject,
                        InjectPosition.MIDDLE,
                    );
                    if (middleContent) {
                        // Return a tag descriptor instead of modifying HTML directly
                        return [
                            {
                                tag: 'script',
                                attrs: {
                                    type: 'module',
                                    src: `/@id/__datadog-helper-file`,
                                },
                                injectTo: 'head-prepend',
                            },
                        ];
                    }
                    return [];
                },
            },
        },
    };

    // We need to handle the resolution in xpack,
    // and it's easier to use unplugin's hooks for it.
    if (isXpack(context.bundler.name)) {
        plugin.load = {
            filter: {
                id: INJECTED_FILE_RX,
            },
            handler() {
                return {
                    code: getContentToInject(contentsToInject, InjectPosition.MIDDLE),
                };
            },
        };
    } else {
        // In xpack, we need to prepare the injections BEFORE the build starts.
        // Otherwise, the bundler doesn't have the content when it needs it.
        // So we do it in their specific plugin.
        // Here for all the other non-xpack bundlers.
        plugin.buildStart = async () => {
            // Prepare the injections.
            await prepareInjections(log, injections, contentsToInject, context.buildRoot);

            if (context.bundler.name === 'esbuild' && hasBeforeAfterInjection(contentsToInject)) {
                context.markArtifactsPending();
            }
        };
    }

    return [plugin];
};
